import React from 'react';
import { Link } from 'react-router-dom';
import {
  Info,
  UserCheck,
  Gavel,
  Wallet,
  Truck,
  RotateCcw,
  Mail,
  Plus,
  Minus,
  Search,
} from 'lucide-react';
import '../../../../styles/components/privacy-policy.scss';
import '../../../../styles/components/faq-page.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import i18n from '../../../../i18n';

/*
 * i18n.t(key, 'Default text') — the default is shown until you add the key
 * to your translation files, so the page works immediately.
 */
const getFaqCategories = () => [
  {
    id: 'faq-general',
    label: i18n.t('faq_cat_general', 'General'),
    icon: Info,
    items: [
      {
        q: i18n.t('faq_q_what_is', 'What is Mzadcom?'),
        a: i18n.t(
          'faq_a_what_is',
          'Mzadcom is an online auction platform in the Sultanate of Oman where individuals and businesses can bid on vehicles, equipment, property and other items from government entities, companies and private sellers.'
        ),
      },
      {
        q: i18n.t('faq_q_participate'),
        a: i18n.t('faq_a_participate'),
      },
      {
        q: i18n.t('faq_q_fees', 'Are there any fees for using the platform?'),
        a: i18n.t(
          'faq_a_fees',
          "Registration is free. A buyer's commission, VAT or other fees may apply to specific auctions; these are always displayed on the auction page before you bid."
        ),
      },
    ],
  },
  {
    id: 'faq-account',
    label: i18n.t('faq_cat_account', 'Account & Registration'),
    icon: UserCheck,
    items: [
      {
        q: i18n.t('faq_q_register'),
        a: i18n.t('faq_a_register'),
      },
      {
        q: i18n.t('faq_q_verify', 'Why do I need to verify my identity?'),
        a: i18n.t(
          'faq_a_verify',
          'Verification protects all users by ensuring that bids come from real, accountable people and companies. It is also required by law for certain types of auctions.'
        ),
      },
    ],
  },
  {
    id: 'faq-bidding',
    label: i18n.t('faq_cat_bidding', 'Bidding'),
    icon: Gavel,
    items: [
      {
        q: i18n.t('faq_q_how_bid', 'How do I place a bid?'),
        a: i18n.t(
          'faq_a_how_bid',
          'Open the lot you are interested in, pay the participation deposit if one is required, enter an amount equal to or higher than the minimum increment and confirm your bid.'
        ),
      },
      {
        q: i18n.t('faq_q_retract', 'Can I cancel a bid after placing it?'),
        a: i18n.t(
          'faq_a_retract',
          'No. Every bid is a binding offer to purchase. If you believe you placed a bid in clear error, contact us immediately before the auction closes.'
        ),
      },
      {
        q: i18n.t('faq_q_extension', 'Why did the auction closing time change?'),
        a: i18n.t(
          'faq_a_extension',
          'To give everyone a fair chance, some auctions extend automatically when a bid is placed in the final minutes. The new closing time is shown on the lot page.'
        ),
      },
    ],
  },
  {
    id: 'faq-payments',
    label: i18n.t('faq_cat_payments', 'Payments & Deposits'),
    icon: Wallet,
    items: [
      {
        q: i18n.t('faq_q_payment'),
        a: i18n.t('faq_a_payment'),
      },
      {
        q: i18n.t('faq_q_deposit', 'What is the participation deposit?'),
        a: i18n.t(
          'faq_a_deposit',
          "It is a refundable amount some auctions require before you can bid. If you don't win, it is refunded automatically; if you win, it is deducted from the amount you pay."
        ),
      },
      {
        q: i18n.t('faq_q_secure', 'Is paying on Mzadcom secure?'),
        a: i18n.t(
          'faq_a_secure',
          'Yes. All payments are processed over an encrypted HTTPS connection by a licensed, PCI-DSS compliant payment gateway. We never store your full card details.'
        ),
      },
    ],
  },
  {
    id: 'faq-delivery',
    label: i18n.t('faq_cat_delivery', 'Delivery & Collection'),
    icon: Truck,
    items: [
      {
        q: i18n.t('faq_q_collect', 'How do I collect an item I have won?'),
        a: i18n.t(
          'faq_a_collect',
          'After full payment, you will receive the collection details. Bring your civil ID and payment confirmation to the stated location within the collection period.'
        ),
      },
      {
        q: i18n.t('faq_q_deliver', 'Do you deliver items?'),
        a: i18n.t(
          'faq_a_deliver',
          'Delivery is available for selected lots within Oman and is indicated on the auction page. Please see our Delivery Policy for timelines and fees.'
        ),
      },
    ],
  },
  {
    id: 'faq-refunds',
    label: i18n.t('faq_cat_refunds', 'Refunds & Returns'),
    icon: RotateCcw,
    items: [
      {
        q: i18n.t('faq_q_refund'),
        a: i18n.t('faq_a_refund'),
      },
      {
        q: i18n.t('faq_q_return_item', 'Can I return an item?'),
        a: i18n.t(
          'faq_a_return_item',
          'Items are sold as is, but a return may be accepted if the item is materially different from its description or was damaged before handover. Requests must be made within 48 hours.'
        ),
      },
    ],
  },
];

class FaqPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'faq-general',
      openItem: 'faq-general-0',
      query: '',
    };
    this.observer = null;
    this.clickLock = false;
    this.lockTimer = null;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setupScrollSpy();
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.lockTimer);
  }

  // Highlights the sidebar item for the section currently on screen
  setupScrollSpy = () => {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.clickLock) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) this.setState({ activeSection: visible[0].target.id });
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    this.observeSections();
  };

  observeSections = () => {
    if (!this.observer) return;
    this.observer.disconnect();
    document.querySelectorAll('.faq-group').forEach((el) => this.observer.observe(el));
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.query !== this.state.query) this.observeSections();
  }

  handleSectionClick = (sectionId) => {
    this.clickLock = true;
    this.setState({ activeSection: sectionId });
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    clearTimeout(this.lockTimer);
    this.lockTimer = setTimeout(() => {
      this.clickLock = false;
    }, 800);
  };

  toggleItem = (key) => {
    this.setState((prev) => ({ openItem: prev.openItem === key ? null : key }));
  };

  getFilteredCategories = (categories) => {
    const q = this.state.query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((it) => `${it.q} ${it.a}`.toLowerCase().includes(q)),
      }))
      .filter((cat) => cat.items.length);
  };

  renderAnswer = (text) =>
    String(text || '')
      .split('\n')
      .filter(Boolean)
      .map((line, i) => <p key={i}>{line}</p>);

  render() {
    const { activeSection, openItem, query } = this.state;
    const categories = getFaqCategories();
    const filtered = this.getFilteredCategories(categories);
    const hasQuery = query.trim().length > 0;

    const menuItems = [
      ...categories.map((c) => ({ id: c.id, label: c.label, icon: c.icon, count: c.items.length })),
      { id: 'faq-contact', label: i18n.t('contact_us'), icon: Mail },
    ];

    return (
      <div className="privacy-page faq-page">
        <HeaderBanner title={i18n.t('faq_title')} subtitle={i18n.t('faq_description')} />

        <div className="privacy-container">
          <aside className="privacy-sidebar">
            <nav>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => this.handleSectionClick(item.id)}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                    {item.count ? <span className="faq-count">{item.count}</span> : null}
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="privacy-content">
            <div className="intro-text">
              {i18n.t(
                'faq_intro',
                "Find quick answers about registering, bidding, payments, delivery and refunds. Can't find what you need? Our team is happy to help."
              )}
            </div>

            <label className="faq-search">
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => this.setState({ query: e.target.value })}
                placeholder={i18n.t(
                  'faq_search_placeholder',
                  'Search questions e.g. deposit, refund, delivery…'
                )}
                aria-label={i18n.t('faq_search_placeholder', 'Search questions')}
              />
            </label>

            {filtered.length === 0 && (
              <div className="faq-empty">
                <p>{i18n.t('faq_no_results', 'No questions match your search.')}</p>
                <button type="button" onClick={() => this.setState({ query: '' })}>
                  {i18n.t('faq_clear_search', 'Clear search')}
                </button>
              </div>
            )}

            {filtered.map((cat) => {
              const Icon = cat.icon;
              return (
                <section key={cat.id} id={cat.id} className="privacy-section faq-group">
                  <h2>
                    <span className="faq-group__icon">
                      <Icon size={20} />
                    </span>
                    {cat.label}
                  </h2>

                  <div className="faq-list">
                    {cat.items.map((item, index) => {
                      const key = `${cat.id}-${index}`;
                      const isOpen = hasQuery || openItem === key;
                      return (
                        <div key={key} className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
                          <button
                            type="button"
                            className="faq-item__header"
                            onClick={() => this.toggleItem(key)}
                            aria-expanded={isOpen}
                            aria-controls={`${key}-panel`}
                            id={`${key}-btn`}
                          >
                            <span className="faq-item__question">{item.q}</span>
                            <span className="faq-item__icon">
                              {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                            </span>
                          </button>
                          <div
                            className="faq-item__body"
                            id={`${key}-panel`}
                            role="region"
                            aria-labelledby={`${key}-btn`}
                          >
                            <div className="faq-item__content">{this.renderAnswer(item.a)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            <section id="faq-contact" className="privacy-section faq-group">
              <h2>
                <span className="faq-group__icon">
                  <Mail size={20} />
                </span>
                {i18n.t('faq_still_questions', 'Still have questions?')}
              </h2>
              <div className="contact-box">
                <p>
                  <strong>{i18n.t('group_auction')}</strong>
                </p>
                <p>{i18n.t('office_address_info')}</p>
                <p>
                  Email: <a href="mailto:info@mzadcom.om">info@mzadcom.om</a>
                </p>
                <Link to="/contact" className="faq-contact-btn">
                  {i18n.t('contact_us')}
                </Link>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }
}

export default FaqPage;
